<template>
    <div class="api-keys-settings">
        <div class="container-search">
            <div class="left">
                <h3 class="title">{{ $t('dash.settings.api_keys.title') }}</h3>
                <p class="subtitle">{{ $t('dash.settings.api_keys.subtitle') }}</p>
            </div>
            <div class="right">
                <div class="btn-outline-primary icon-l" @click="goToDocumentation"><i class="fa-solid fa-book"></i><span>{{ $t('dash.settings.api_keys.documentation-button') }}</span></div>
                <div class="btn-primary icon-l" @click="openCreatePopup"><i class="fa-solid fa-plus"></i><span>{{ $t('dash.settings.api_keys.add-button') }}</span></div>
            </div>
        </div>

        <div class="help-panel">
            <div class="help-header">
                <i class="fa-solid fa-circle-info"></i>
                <p>{{ $t('dash.settings.api_keys.help.intro') }}</p>
            </div>

            <div class="help-options">
                <div class="help-option">
                    <div class="help-option-icon"><i class="fa-solid fa-key"></i></div>
                    <div class="help-option-body">
                        <p class="help-option-title">{{ $t('dash.settings.api_keys.help.manual_title') }}</p>
                        <p class="help-option-text">{{ $t('dash.settings.api_keys.help.manual_text') }}</p>
                        <a class="help-option-link" @click="openCreatePopup">{{ $t('dash.settings.api_keys.help.manual_link') }}</a>
                    </div>
                </div>
                <div class="help-option">
                    <div class="help-option-icon"><i class="fa-solid fa-plug"></i></div>
                    <div class="help-option-body">
                        <p class="help-option-title">{{ $t('dash.settings.api_keys.help.oauth_title') }}</p>
                        <p class="help-option-text">{{ $t('dash.settings.api_keys.help.oauth_text') }}</p>
                    </div>
                </div>
            </div>

            <div class="help-endpoint">
                <span class="label">{{ $t('dash.settings.api_keys.help.endpoint_label') }}</span>
                <div class="api-url-field">
                    <input type="text" readonly :value="mcpEndpoint" @click="$event.target.select()">
                    <button class="api-url-copy" @click="copyEndpoint" :title="$t('dash.settings.api_keys.help.endpoint_copy')">
                        <i :class="endpointCopied ? 'fa-solid fa-check' : 'fa-solid fa-copy'"></i>
                    </button>
                </div>
            </div>
        </div>

        <div class="container-list-api-keys" v-if="apiKeys.length">
            <div class="legend">
                <p class="name">{{ $t('dash.settings.api_keys.table.name') }}</p>
                <p class="key">{{ $t('dash.settings.api_keys.table.key') }}</p>
                <p class="scopes">{{ $t('dash.settings.api_keys.table.scopes') }}</p>
                <p class="pages">{{ $t('dash.settings.api_keys.table.pages') }}</p>
                <p class="expires">{{ $t('dash.settings.api_keys.table.expires') }}</p>
                <p class="status">{{ $t('dash.settings.api_keys.table.status') }}</p>
                <p class="actions"></p>
            </div>

            <div class="api-key" v-for="apiKey in apiKeys" :key="apiKey.id">
                <p class="name">{{ apiKey.name }}</p>
                <p class="key color-grey">{{ apiKey.key_prefix }}&hellip;</p>
                <div class="scopes">
                    <span class="badge-outline-r" v-for="scope in apiKey.scopes" :key="scope">{{ scopeLabel(scope) }}</span>
                </div>
                <p class="pages color-grey">{{ pagesLabel(apiKey) }}</p>
                <p class="expires color-grey">{{ formatDate(apiKey.expires_at) }}</p>
                <div class="status">
                    <span :class="['badge-status', statusOf(apiKey)]">{{ $t('dash.settings.api_keys.status.' + statusOf(apiKey)) }}</span>
                </div>
                <div class="actions">
                    <template v-if="statusOf(apiKey) === 'active'">
                        <div class="btn-outline-primary icon-l" @click="openEditPopup(apiKey)">
                            <i class="fa-solid fa-pen"></i><span>{{ $t('dash.settings.api_keys.edit') }}</span>
                        </div>
                        <div class="btn-outline-primary icon-l" @click="onRevoke(apiKey)">
                            <i class="fa-regular fa-trash-can"></i><span>{{ $t('dash.settings.api_keys.revoke') }}</span>
                        </div>
                    </template>
                    <div class="btn-outline-primary icon-l" @click="onDelete(apiKey)" v-else>
                        <i class="fa-regular fa-trash-can"></i><span>{{ $t('dash.settings.api_keys.delete') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <p class="empty" v-else>{{ $t('dash.settings.api_keys.no-keys') }}</p>

        <ApiKeyPopup
            v-if="displayPopup"
            :display="displayPopup"
            :accountPages="allPages"
            :data="editingApiKey"
            :oauthMode="!!oauthFlowId"
            :defaultName="oauthDefaultName"
            @closePopUp="closePopup"
            @submit="onSubmitApiKey"
        ></ApiKeyPopup>

        <div class="created-key-reveal" v-if="createdKeyReveal">
            <div class="popup">
                <h2>{{ $t('dash.settings.api_keys.created.title') }}</h2>
                <p class="warning"><i class="fa-solid fa-triangle-exclamation"></i>{{ $t('dash.settings.api_keys.created.warning') }}</p>
                <div class="key-value">
                    <code>{{ createdKeyReveal.key }}</code>
                    <div class="btn-outline-primary" @click="copyCreatedKey">{{ copied ? $t('dash.settings.api_keys.created.copied') : $t('dash.settings.api_keys.created.copy') }}</div>
                </div>
                <div class="btns">
                    <div class="btn-primary" @click="closeCreatedKeyReveal">{{ $t('dash.settings.api_keys.created.close') }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import ApiKeyPopup from './api-key-popup.vue'
import ApiKeysMixin from '../../mixins/ApiKeys.vue'

const SCOPE_LABELS = {
    'bots:read': 'dash.settings.api_keys.scope.bots_read',
    'bots:write': 'dash.settings.api_keys.scope.bots_write',
    'posts:read': 'dash.settings.api_keys.scope.posts_read',
    'posts:write': 'dash.settings.api_keys.scope.posts_write',
    'moderation:read': 'dash.settings.api_keys.scope.moderation_read',
    'moderation:write': 'dash.settings.api_keys.scope.moderation_write',
    'reporting:read': 'dash.settings.api_keys.scope.reporting_read',
    'sources:read': 'dash.settings.api_keys.scope.sources_read',
    'sources:write': 'dash.settings.api_keys.scope.sources_write',
}

export default {
    name: 'api-keys-settings',

    mixins: [ApiKeysMixin],

    components: {
        ApiKeyPopup,
    },

    props: {
        allPages: {
            type: Array,
            default: () => []
        },
        infoUser: {
            type: Object,
            default: () => ({})
        },
    },

    data() {
        return {
            apiKeys: [],
            displayCreatePopup: false,
            editingApiKey: null,
            createdKeyReveal: null,
            copied: false,
            oauthFlowId: null,
            endpointCopied: false,
        };
    },

    computed: {
        displayPopup() {
            return this.displayCreatePopup || !!this.editingApiKey
        },

        oauthDefaultName() {
            if (!this.oauthFlowId) return ''
            // `client` comes from oauth.js::authorize (derived from the connecting client's own
            // redirect_uri host - see its own comment for why that's used instead of client_name)
            // - falls back to "MCP" for an older link or an unrecognized/unlabeled client, rather
            // than assuming "Claude" like this used to.
            const client = this.$route.query.client || 'MCP'
            const person = [this.infoUser.firstname, this.infoUser.lastname].filter(Boolean).join(' ').trim()
            return [client, person, moment().format('DD/MM/YYYY')].filter(Boolean).join(' - ')
        },

        // Same VUE_APP_API_HOST used everywhere else to reach the engine (see e.g.
        // section/Pages.vue::isProd) - the MCP server shares that same host/domain (routed by
        // path, see the deployment notes), so this always points at the right environment
        // without hardcoding "sandbox" here.
        mcpEndpoint() {
            return `${process.env.VUE_APP_API_HOST}/mcp`
        }
    },

    async mounted() {
        this.refreshApiKeys()

        // OAuth "remote connector" flow (engine's /oauth/authorize redirects here): open the
        // create popup right away, completing it also authorizes the connection.
        if (this.$route.query.oauth_flow) {
            this.oauthFlowId = this.$route.query.oauth_flow
            this.openCreatePopup()
        }
    },

    methods: {
        refreshApiKeys() {
            this.listApiKeys().then(res => {
                const { success, api_keys } = res.body || {}
                if (success !== true) return
                this.apiKeys = api_keys
            })
        },

        scopeLabel(scope) {
            return this.$t(SCOPE_LABELS[scope] || scope)
        },

        pagesLabel(apiKey) {
            if (!apiKey.pages_access || !apiKey.pages_access.length) return this.$t('dash.settings.api_keys.pages.all')
            return `${apiKey.pages_access.length}`
        },

        statusOf(apiKey) {
            if (apiKey.revoked_at) return 'revoked'
            if (apiKey.expires_at && moment(apiKey.expires_at).isBefore(moment())) return 'expired'
            return 'active'
        },

        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : ''
        },

        goToDocumentation() {
            this.$router.push('/dash/settings/mcp_docs')
        },

        openCreatePopup() {
            this.editingApiKey = null
            this.displayCreatePopup = true
        },

        openEditPopup(apiKey) {
            this.displayCreatePopup = false
            this.editingApiKey = apiKey
        },

        closePopup() {
            this.displayCreatePopup = false
            this.editingApiKey = null
            this.oauthFlowId = null
        },

        async onSubmitApiKey(payload) {
            if (this.oauthFlowId) return this.onSubmitOAuthApiKey(payload)

            const res = payload.id
                ? await this.updateApiKey(payload.id, payload)
                : await this.createApiKey(payload)

            const { success, api_key } = res.body || {}

            if (success !== true) {
                this.$vs.notify({
                    title: this.$t('dash.notifications.error_title'),
                    text: this.$t('dash.notifications.error_body'),
                    color: 'error'
                })
                return
            }

            this.closePopup()

            // the raw key is only ever returned on creation - reveal it once
            if (!payload.id) {
                this.createdKeyReveal = api_key
                this.copied = false
            }

            this.refreshApiKeys()
        },

        async onSubmitOAuthApiKey(payload) {
            const res = await this.completeOAuthApiKey(this.oauthFlowId, payload)
            const { success, redirect_uri } = res.body || {}

            if (success !== true || !redirect_uri) {
                this.$vs.notify({
                    title: this.$t('dash.notifications.error_title'),
                    text: this.$t('dash.notifications.error_body'),
                    color: 'error'
                })
                return
            }

            // hand control back to the connecting client (e.g. Claude) - the key itself is
            // never shown here, it travels directly through the OAuth token exchange instead.
            window.location.href = redirect_uri
        },

        closeCreatedKeyReveal() {
            this.createdKeyReveal = null
        },

        copyCreatedKey() {
            const el = document.createElement('textarea');
            el.value = this.createdKeyReveal.key;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            document.body.appendChild(el);
            const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            if (selected) {
                document.getSelection().removeAllRanges();
                document.getSelection().addRange(selected);
            }
            this.copied = true
        },

        copyEndpoint() {
            const el = document.createElement('textarea');
            el.value = this.mcpEndpoint;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            document.body.appendChild(el);
            const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false;
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            if (selected) {
                document.getSelection().removeAllRanges();
                document.getSelection().addRange(selected);
            }
            this.endpointCopied = true
            setTimeout(() => { this.endpointCopied = false }, 2000)
        },

        async onRevoke(apiKey) {
            if (!confirm(this.$t('dash.settings.api_keys.revoke-confirm'))) return

            const res = await this.revokeApiKey(apiKey.id)
            const { success } = res.body || {}

            if (success !== true) {
                this.$vs.notify({
                    title: this.$t('dash.notifications.error_title'),
                    text: this.$t('dash.notifications.error_body'),
                    color: 'error'
                })
                return
            }

            this.refreshApiKeys()
        },

        async onDelete(apiKey) {
            if (!confirm(this.$t('dash.settings.api_keys.delete-confirm'))) return

            // same endpoint as revoke: the backend permanently deletes a key that's already
            // expired/revoked instead of soft-revoking it again (nothing left to protect).
            const res = await this.revokeApiKey(apiKey.id)
            const { success } = res.body || {}

            if (success !== true) {
                this.$vs.notify({
                    title: this.$t('dash.notifications.error_title'),
                    text: this.$t('dash.notifications.error_body'),
                    color: 'error'
                })
                return
            }

            this.refreshApiKeys()
        },
    },
};
</script>

<style lang="scss" scoped>
.api-keys-settings {
    .container-search {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 16px;

        .title {
            font-weight: 600;
            font-size: 16px;
            margin: 0 0 4px;
        }

        .subtitle {
            color: #9ba3b7;
            font-size: 13px;
            margin: 0;
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .help-panel {
        background: #faf8fc;
        border: 1px solid #F1EDF5;
        border-radius: 10px;
        padding: 16px 18px;
        margin-bottom: 16px;

        .help-header {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin-bottom: 14px;

            i {
                color: #6B1F4D;
                font-size: 15px;
                margin-top: 2px;
            }

            p {
                margin: 0;
                font-size: 13px;
                color: #555E75;
                line-height: 1.5;
            }
        }

        .help-options {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 16px;

            @media (max-width: 700px) {
                grid-template-columns: 1fr;
            }
        }

        .help-option {
            display: flex;
            gap: 10px;
            background: #fff;
            border: 1px solid #F1EDF5;
            border-radius: 8px;
            padding: 12px;

            .help-option-icon {
                flex-shrink: 0;
                width: 30px;
                height: 30px;
                border-radius: 50%;
                background: #f9f0f5;
                display: flex;
                align-items: center;
                justify-content: center;

                i {
                    color: #6B1F4D;
                    font-size: 13px;
                }
            }

            .help-option-title {
                margin: 0 0 2px;
                font-size: 13px;
                font-weight: 600;
                color: #121826;
            }

            .help-option-text {
                margin: 0;
                font-size: 12px;
                color: #9FA4A8;
                line-height: 1.4;
            }

            .help-option-link {
                display: inline-block;
                margin-top: 6px;
                font-size: 12px;
                font-weight: 600;
                color: #6B1F4D;
                cursor: pointer;

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        .help-endpoint {
            .label {
                display: block;
                font-size: 12px;
                font-weight: 600;
                color: #121826;
                margin-bottom: 6px;
            }

            .api-url-field {
                display: flex;
                align-items: center;
                gap: 6px;
                border: 1.5px solid #e0d0ea;
                border-radius: 8px;
                padding: 4px 4px 4px 10px;
                background: #fff;
                max-width: 480px;

                input {
                    flex: 1;
                    border: none;
                    background: transparent;
                    font-size: 12px;
                    color: #555e75;
                    outline: none;
                    font-family: monospace;
                    min-width: 0;
                }

                .api-url-copy {
                    display: flex;
                    align-items: center;
                    padding: 6px 9px;
                    border: none;
                    background: #6B1F4D;
                    color: #fff;
                    border-radius: 6px;
                    cursor: pointer;
                    flex-shrink: 0;
                    font-size: 12px;
                    transition: opacity .15s;

                    &:hover {
                        opacity: .85;
                    }
                }
            }
        }
    }

    .container-list-api-keys {
        border: 1px solid #e3e6ee;
        border-radius: 8px;
        overflow: hidden;

        .legend, .api-key {
            display: grid;
            // Colonne "actions" en largeur fixe (pas "auto") : sinon sa taille dépend du
            // contenu de CHAQUE ligne prise isolément (boutons présents ou non selon le statut),
            // ce qui décale les autres colonnes d'une ligne à l'autre.
            grid-template-columns: 1.2fr 1fr 2fr 0.8fr 0.8fr 0.8fr 210px;
            align-items: center;
            gap: 12px;
            padding: 10px 14px;
        }

        .actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
        }

        .legend {
            background: #f9fafc;
            font-weight: 600;
            font-size: 13px;
            border-bottom: 1px solid #e3e6ee;
        }

        .api-key {
            border-bottom: 1px solid #eef0f5;
            font-size: 13px;

            &:last-child {
                border-bottom: none;
            }
        }

        .color-grey {
            color: #9ba3b7;
        }

        .scopes {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;
        }

        .badge-status {
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 11px;

            &.active {
                background: #e5f7ec;
                color: #1f9254;
            }

            &.expired, &.revoked {
                background: #fbeaea;
                color: #c0392b;
            }
        }
    }

    .empty {
        color: #9ba3b7;
        font-size: 13px;
    }

    .created-key-reveal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;

        .popup {
            background: #fff;
            border-radius: 12px;
            padding: 24px;
            width: 480px;
            max-width: 90vw;
        }

        .warning {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #b8860b;
            font-size: 13px;
            margin: 8px 0 16px;
        }

        .key-value {
            display: flex;
            align-items: center;
            gap: 8px;
            background: #f5f6fa;
            border-radius: 6px;
            padding: 10px 12px;
            margin-bottom: 16px;

            code {
                flex-grow: 1;
                font-size: 12px;
                word-break: break-all;
            }
        }

        .btns {
            display: flex;
            justify-content: flex-end;
        }
    }
}
</style>
